"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { ChartColumn, Route, WandSparkles, type LucideIcon } from "lucide-react";
import {
  CornerRibbon,
  DARK_BAND,
  GradText,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useRefGridMode,
  useRibbonAnchor,
  useWholeRowBlocks,
} from "./kit";
import { useEnterStagger } from "./kit/motion";

const POINTS: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: WandSparkles,
    title: "Links from a sentence",
    body: '"Shorten this and route DE traffic to /de" becomes a real, live link — no dashboard clicks.',
  },
  {
    icon: Route,
    title: "Update & route in place",
    body: "Agents repoint destinations and add rules through typed tool calls — QR codes follow automatically.",
  },
  {
    icon: ChartColumn,
    title: "Analytics on demand",
    body: "Ask for clicks, geos or top links and get structured results back into the conversation.",
  },
];

const TOOLS = [
  "create_link",
  "list_links",
  "update_link",
  "delete_link",
  "list_domains",
  "render_qr",
  "link_stats",
];

function ToolCall({ name, dark, children }: { name: string; dark: boolean; children: ReactNode }) {
  return (
    <div
      className={`my-1 overflow-hidden rounded-[11px] border ${
        dark ? "border-white/10 bg-[#0d0d12]" : "border-line-2 bg-surface"
      }`}
    >
      <div
        className={`flex items-center gap-[9px] border-b px-3 py-[9px] font-jetbrains text-xs ${
          dark ? "border-white/10 bg-white/[0.04]" : "border-line bg-surface-2"
        }`}
      >
        <span className="text-data-violet">✦</span>
        <span className={`font-semibold ${dark ? "text-white" : "text-ink"}`}>{name}</span>
        <span className="ml-auto font-semibold text-data-green">✓ done</span>
      </div>
      <pre
        className={`m-0 overflow-auto px-3.5 py-[11px] font-jetbrains text-[11.5px] leading-[1.6] ${
          dark ? "text-white/60" : "text-muted"
        }`}
      >
        {children}
      </pre>
    </div>
  );
}

const K = "text-brand-600";
const S = "text-data-green";
const N = "text-data-blue";

/** Screen-reader summary of the mock conversation — the mockup card itself is
 *  `aria-hidden` (decorative UI, like DomainWinsSection's vignette visuals). */
const CHAT_SUMMARY =
  "Example conversation: the user asks the assistant to create a branded link for the summer sale that routes iPhone users to the App Store, and to show last week's clicks. The assistant calls the create_link tool to create rii.link/summer-sale with iOS traffic routed to the App Store, then calls link_stats, which returns 18,423 clicks over the last 7 days with Germany as the top geography, and confirms the link is live with those results. " +
  "MCP link management is live — shipped August 2026, ahead of its promised date.";

/**
 * Model Context Protocol pitch: chat mockup showing an agent creating and
 * analyzing a link via typed tool calls, plus the "why this matters" points
 * and the tool list. Shared between /developers (light, `band="none"`) and
 * /ai-mcp (`band="dark"`, inside grid-mode `RefSections onGrid`).
 */
export default function McpSection({ band = "none" }: { band?: "dark" | "none" }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid
  const onGrid = useRefGridMode();
  const ribbonWrapRef = useRef<HTMLDivElement>(null);
  // Dark chrome only applies once the surrounding band is actually painted
  // (grid mode) — same double-gate as UseCasesSection, so a stray band="dark"
  // off-grid can't flip text/surfaces to light-on-dark with no dark surface
  // underneath.
  const dark = onGrid && band === "dark";

  useEnterStagger(rootRef, "[data-mcp-col]", { y: 24, stagger: 0.12 });
  // useWholeRowBlocks/useBandRowAlign pad and re-align [data-grid-block]s for
  // grid-paper layout — running them on /developers (non-grid RefSections)
  // would inject inline padding/margins into this SHARED section and break
  // "visually unchanged". Hooks can't be called conditionally, so gate them by
  // ref instead: both bail immediately once `innerRef.current` is null (their
  // layout() starts from `innerRef.current?.closest("section")`), so pointing
  // them at `nullRef` (never attached to any element) off-grid is a full
  // no-op rather than a skipped hook call.
  useWholeRowBlocks(onGrid ? rootRef : nullRef);
  useBandRowAlign(onGrid ? rootRef : nullRef);

  // Corner ribbon anchor at the title box's corner (grid clearing in grid
  // mode, the head block's own box off-grid) — see useRibbonAnchor.
  const ribbonPos = useRibbonAnchor(ribbonWrapRef, onGrid);

  return (
    <SectionShell
      id="ref-mcp"
      divider
      band={dark ? "dark" : undefined}
      decoAvoid
      className={dark ? "max-md:bg-[#14141c] md:pt-px md:pb-px" : undefined}
    >
      {/* Plain block wrapper (no chrome of its own): the ribbon's position
          is computed imperatively in the layout effect above (grid mode:
          the head's GRID CLEARING corner; off-grid: the head block's own
          box corner) and passed down as an inline style override. */}
      <div ref={ribbonWrapRef} className="relative">
        {/* The roadmap ribbon kept its promise: promised Sept 2026, shipped
            Aug 2026 (lib/roadmap.ts) — same anchor, flipped to green. The
            receipts live on /changelog. */}
        <CornerRibbon
          label="Live · Aug 2026"
          variant="green"
          style={{ top: ribbonPos.top, right: ribbonPos.right }}
        />
        <SectionHead
          eyebrow="Model Context Protocol"
          tone={dark ? "light" : "default"}
          gridFill={dark ? DARK_BAND : undefined}
          title={
            <>
              Let agents <GradText>create, update &amp; analyze</GradText> links for you.
            </>
          }
          lead="Connect https://mcp.rii.link with OAuth and your AI assistant manages links the way it manages files — each agent stays visible, scoped and revocable in your rii.link account."
        />
      </div>

      <div
        ref={rootRef}
        data-grid-block
        data-grid-fill={dark ? DARK_BAND : undefined}
        className={`grid ${dark ? "items-center" : "items-start"} gap-[46px] lg:grid-cols-[1.45fr_1fr]`}
      >
        <div data-mcp-col className="min-w-0">
          <p className="sr-only">{CHAT_SUMMARY}</p>
          <div
            aria-hidden
            className={`overflow-hidden rounded-ref-lg border shadow-ref-lg ${
              dark ? "border-white/10 bg-[#101017]" : "border-line bg-surface"
            }`}
          >
            <div
              className={`flex items-center gap-[11px] border-b px-[18px] py-3.5 ${
                dark ? "border-white/10 bg-white/[0.04]" : "border-line bg-surface-2"
              }`}
            >
              <span className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-[linear-gradient(140deg,var(--color-data-violet),#4a32a8)] text-[15px] text-white">
                ✦
              </span>
              <div>
                <div className={`text-sm font-semibold ${dark ? "text-white" : ""}`}>Assistant</div>
                <div className={`text-xs ${dark ? "text-white/60" : "text-muted"}`}>
                  connected to rii.link MCP
                </div>
              </div>
              <span className="ml-auto rounded-[7px] border border-data-violet/30 bg-data-violet/10 px-[9px] py-1 font-jetbrains text-[11px] text-data-violet">
                rii-mcp · OAuth
              </span>
            </div>
            <div className="flex min-h-[380px] flex-col gap-3.5 p-[18px]">
              <div className="max-w-[90%] self-end rounded-[13px_13px_4px_13px] bg-brand-600 px-3.5 py-2.5 text-sm text-white">
                Create a branded link for the summer sale that sends iPhone users to the App Store, and show me
                last week&apos;s clicks.
              </div>
              <div className={`max-w-[90%] self-start text-sm ${dark ? "text-white/90" : "text-ink"}`}>
                On it — creating the link with iOS routing, then pulling last week&apos;s clicks.
              </div>
              <ToolCall name="create_link" dark={dark}>
                {"{ "}
                <span className={K}>&quot;url&quot;</span>: <span className={S}>&quot;https://myshop.com/summer&quot;</span>,{" "}
                <span className={K}>&quot;slug&quot;</span>: <span className={S}>&quot;summer-sale&quot;</span>,{" "}
                <span className={K}>&quot;routing&quot;</span>: {"[{ "}
                <span className={K}>&quot;if&quot;</span>: {"{ "}
                <span className={K}>&quot;device&quot;</span>: <span className={S}>&quot;ios&quot;</span>
                {" }, "}
                <span className={K}>&quot;to&quot;</span>:{" "}
                <span className={S}>&quot;apps.apple.com/id123&quot;</span> {"}] }"}
              </ToolCall>
              <ToolCall name="link_stats" dark={dark}>
                {"{ "}
                <span className={K}>&quot;total&quot;</span>: <span className={N}>18423</span>,{" "}
                <span className={K}>&quot;buckets&quot;</span>: {"[{ "}
                <span className={K}>&quot;key&quot;</span>: <span className={S}>&quot;DE&quot;</span>, … {"}, …] }"}
              </ToolCall>
              <div className={`max-w-[90%] self-start text-sm ${dark ? "text-white/90" : "text-ink"}`}>
                Done — <span className="font-jetbrains text-brand-600">rii.link/summer-sale</span> is live (iOS
                → App Store). Last week: <b className="font-semibold">18,423</b> clicks, top geo{" "}
                <b className="font-semibold">DE</b>.
              </div>
            </div>
          </div>
        </div>

        <div data-mcp-col className="min-w-0">
          <div className="flex flex-col gap-[18px]">
            {POINTS.map((pt) => (
              <div key={pt.title} className="flex gap-3.5">
                <div
                  className={`grid h-10 w-10 shrink-0 place-items-center rounded-[11px] border text-brand-600 ${
                    dark ? "border-brand-600/40 bg-brand-600/[0.15]" : "border-brand-600/30 bg-brand-600/[0.08]"
                  }`}
                >
                  <pt.icon size={19} strokeWidth={2} aria-hidden />
                </div>
                <div>
                  <h4 className={`mb-1 text-[16.5px] font-semibold ${dark ? "text-white" : ""}`}>{pt.title}</h4>
                  <p className={`text-[14.5px] ${dark ? "text-white/70" : "text-muted"}`}>{pt.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-[18px] flex flex-wrap gap-2">
            {TOOLS.map((t) => (
              <span
                key={t}
                className={`rounded-lg border px-[11px] py-1.5 font-jetbrains text-xs ${
                  dark ? "border-white/10 bg-white/[0.05] text-white/60" : "border-line bg-surface-2 text-muted"
                }`}
              >
                <b className="text-data-violet">{t}</b>
              </span>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
